import { useCheckboxGroup, useCheckboxGroupItem } from '@react-aria/checkbox';
import { useCheckboxGroupState } from '@react-stately/checkbox';
import type { AriaCheckboxGroupItemProps, CheckboxGroupProps } from '@react-types/checkbox';
import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';
import cx from 'classnames';
import type { RefObject } from 'react';
import React from 'react';
interface IProps extends IFieldProps, CheckboxGroupProps {
  displayMember: string;
  valueMember: string;
  children?: any;
  label?: string;
  options: any[];
  selected: any[];
  column?: number;
  inputClassName?: string;
  labelClassName?: string;
  handleChange: (checked: boolean, name: any) => void;
}

interface ICheckboxGroupItem extends AriaCheckboxGroupItemProps {
  valueMember: string;
  inputClassName?: string;
  column?: any;
  option: any;
  selected: any[];
  handleChange: (checked: boolean, option: any) => void;
  testId?: string;
}

let CheckboxGroupContext = React.createContext(null);

function AriaCheckBoxGroup(props: IProps) {
  let { children, label, labelClassName } = props;
  let state: any = useCheckboxGroupState(props);
  let { groupProps, labelProps } = useCheckboxGroup(props, state);

  return (
    <div {...groupProps}>
      <div className={labelClassName}>
        {label && (
          <label {...labelProps} className="font-semibold">
            {label}
          </label>
        )}
      </div>
      <div className="flex w-full flex-wrap">
        <CheckboxGroupContext.Provider value={state}>{children}</CheckboxGroupContext.Provider>
      </div>
    </div>
  );
}

function CheckboxGroupItem(props: ICheckboxGroupItem) {
  let { children, option, selected, valueMember, column, inputClassName, handleChange, testId } =
    props;
  let state: any = React.useContext(CheckboxGroupContext);
  let ref = React.useRef(null);
  let { inputProps } = useCheckboxGroupItem(
    {
      ...props
    },
    state,
    ref
  );
  const { type } = inputProps;

  return (
    <div
      className="mb-1 flex items-center text-sm font-medium"
      style={{ width: `${column ? 100 / column : 100}%` }}
    >
      <input
        {...inputProps}
        data-testid={testId}
        type={type}
        onChange={(e) => handleChange(Boolean(e.target.value), option)}
        checked={
          option.hasOwnProperty(valueMember)
            ? selected
                .map((option) =>
                  option.hasOwnProperty(valueMember) ? option[valueMember] : option
                )
                .includes(option[valueMember])
            : selected.includes(option)
        }
        ref={ref}
        className={cx(
          inputClassName,
          'mr-3 rounded border-gray-400 text-clickBlue focus:ring-clickBlue'
        )}
      />
      <label className="p-checkbox-label">{children}</label>
    </div>
  );
}

class CheckBoxGroup extends React.Component<IProps> {
  static defaultProps = {
    displayMember: 'name',
    valueMember: 'name',
    options: [],
    selected: [],
    handleChange: null
  };

  constructor(props: IProps) {
    super(props);
  }

  render() {
    const { options, selected, displayMember, valueMember, handleChange, column, inputClassName } =
      this.props;
    return (
      <AriaCheckBoxGroup {...this.props}>
        {options.map((option: any, index: number) => {
          return (
            <CheckboxGroupItem
              testId={option.hasOwnProperty(displayMember) ? option[displayMember] : option}
              key={`cb-${index}`}
              valueMember={valueMember}
              value={option.hasOwnProperty(valueMember) ? option[valueMember] : option}
              option={option}
              column={column}
              selected={selected}
              handleChange={handleChange}
              inputClassName={inputClassName}
            >
              {option.hasOwnProperty(displayMember) ? option[displayMember] : option}
            </CheckboxGroupItem>
          );
        })}
      </AriaCheckBoxGroup>
    );
  }
}

export default CheckBoxGroup;
